import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CompanySettings, CompanySettingsUpdate } from '~/shared/data-contract/company-settings';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { companySettingsKeys } from './query-keys';
import { useCompanySettingsInvalidator } from './use-company-settings-invalidator';

type UseUpdateCompanySettingsParams = {
    readonly payload: CompanySettingsUpdate;
};

export type UseUpdateCompanySettingsResult = UseMutationResult<CompanySettings, Error, UseUpdateCompanySettingsParams>;

type MutationContext = {
    readonly previousSettings: CompanySettings | undefined;
};

export const useUpdateCompanySettings = (): UseUpdateCompanySettingsResult => {
    const { updateCompanySettings } = useApplicationFetchers();
    const { invalidateCompanySettings } = useCompanySettingsInvalidator();
    const queryClient = useQueryClient();

    return useMutation<CompanySettings, Error, UseUpdateCompanySettingsParams, MutationContext>({
        mutationFn: async ({ payload }: UseUpdateCompanySettingsParams) => {
            const result = await updateCompanySettings({ payload });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onError: (_error, _variables, context) => {
            if (!is.undefined(context?.previousSettings)) {
                queryClient.setQueryData<CompanySettings>(
                    companySettingsKeys.companySettings(),
                    context.previousSettings,
                );
            }
        },
        onMutate: async ({ payload }) => {
            await queryClient.cancelQueries({
                queryKey: companySettingsKeys.companySettings(),
            });

            const previousSettings = queryClient.getQueryData<CompanySettings>(companySettingsKeys.companySettings());

            queryClient.setQueryData<CompanySettings>(companySettingsKeys.companySettings(), (oldSettings) => {
                if (is.undefined(oldSettings)) {
                    return oldSettings;
                }

                return {
                    ...oldSettings,
                    ...payload,
                    jobArchitectureTerminology:
                        payload.jobArchitectureTerminology ?? oldSettings.jobArchitectureTerminology,
                    skillProficiencyLevelTerminology:
                        payload.skillProficiencyLevelTerminology ?? oldSettings.skillProficiencyLevelTerminology,
                };
            });

            return { previousSettings };
        },
        onSuccess: () => {
            invalidateCompanySettings();
        },
        throwOnError: false,
    });
};
